import { Injectable } from '@angular/core';
import { DashboardClaim, Guid } from '@lsq-frontend/remote-services/enterprise/common';
import { ClaimService, FeatureFlagService } from '@lsq-frontend/common-ui';
import {
    BankAccount,
    CreateBankAccountForSellerRequest,
    CreateInternationalBankAccountForSellerService,
    CreateBankAccountForSellerService,
    CreateInternationalBankAccountForSellerRequest,
    DeleteBankAccountForSellerRequest,
    DeleteBankAccountForSellerService,
    ListBankAccountForSellerService,
    ListRedactedBankAccountForSellerRequest,
    RedactedBankAccount,
    SubmitBankAccountForVerificationRequest,
    SubmitBankAccountForVerificationService,
    UpdateRelationshipBankAccountRequest,
    UpdateRelationshipBankAccountService,
    UpdateSellerDefaultBankAccountRequest,
    UpdateSellerDefaultBankAccountService,
    AccountRequestAddress
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';

import {
    PostPaymentMethodService,
    DeletePaymentMethodService,
    PutPaymentMethodService,
    GetPaymentMethodByCompanyKeyService
} from '@lsq-frontend/remote-services/pmt-restapi';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';

import { Pagination } from '@lsq-frontend/angular-shared/api-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { TAKE_ALL } from '../constants';
import { SupplyChainPlatformRelationshipService } from '../platform/relationship/relationship.service';
import { Address } from '@lsq-frontend/remote-services/all-java/common-mk0';

import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

const DIRECT = 'DIRECT';
const PLATFORM = 'PLATFORM';
@Injectable({ providedIn: 'root' })
export class BankAccountsService {
    private _bankAccountsListInitializedSubject = new BehaviorSubject<boolean>(false);
    public bankAccountsListInitialized$ = this._bankAccountsListInitializedSubject.asObservable();

    private _bankAccountsSubject = new BehaviorSubject<RedactedBankAccount[]>([]);
    public bankAccounts$ = this._bankAccountsSubject.asObservable().pipe(
        // Keep the default account at the top.
        map((bankAccounts) => {
            const bankAccountsCopy = [...bankAccounts];
            return bankAccountsCopy.sort(({ isDefault }) => (isDefault ? -1 : 1));
        })
    );

    public hasVerifiedBankAccount$: Observable<boolean>;

    constructor(
        private _claimService: ClaimService,
        private _listBankAccountForSellerService: ListBankAccountForSellerService,
        private _deleteBankAccountForSellerService: DeleteBankAccountForSellerService,
        private _updateSellerDefaultBankAccountService: UpdateSellerDefaultBankAccountService,
        private _createBankAccountForSellerService: CreateBankAccountForSellerService,
        private _createInternationalBankAccountForSellerService: CreateInternationalBankAccountForSellerService,
        private _submitBankAccountForVerificationService: SubmitBankAccountForVerificationService,
        private _updateRelationshipBankAccountService: UpdateRelationshipBankAccountService,
        private _platformRelationshipService: SupplyChainPlatformRelationshipService,
        private _postPaymentMethodService: PostPaymentMethodService,
        private _userService: UserService,
        private _deletePaymentMethodService: DeletePaymentMethodService,
        private _putPaymentMethodService: PutPaymentMethodService,
        private _featureFlagService: FeatureFlagService,
        private _getPaymentMethodByCompanyKeyService: GetPaymentMethodByCompanyKeyService
    ) {}

    public get userType(): string {
        return this._claimService.isOnSync(DashboardClaim.FinancingPlatformEnrollment)
            ? PLATFORM
            : DIRECT;
    }

    public async listBankAccounts(paginationUpdate: Partial<Pagination> = {}): Promise<void> {
        switch (this.userType) {
            case DIRECT: {
                const request = new ListRedactedBankAccountForSellerRequest({
                    pagination: new Pagination({
                        ...paginationUpdate,
                        take: TAKE_ALL
                    })
                });
                this._bankAccountsSubject.next(
                    await this._listBankAccountForSellerService
                        .executeOnce(request)
                        .then(({ redactedBankAccount }) => redactedBankAccount)
                );
                break;
            }
            case PLATFORM: {
                this.bankAccounts$ = this._platformRelationshipService.bankAccounts$.pipe(
                    map((bankAccounts) => {
                        const bankAccountsCopy = [...bankAccounts];
                        return bankAccountsCopy.sort(({ isDefault }) => (isDefault ? -1 : 0));
                    })
                );
            }
        }

        this.hasVerifiedBankAccount$ = this.bankAccounts$.pipe(
            map((bankAccounts) => bankAccounts.some((bankAccount) => bankAccount.isVerified))
        );

        this._bankAccountsListInitializedSubject.next(true);
    }

    public async createBankAccount(
        bankAccount: BankAccount,
        isEnrolling = false,
        options?: { sellerKey: string }
    ): Promise<Guid> {
        if (isEnrolling) {
            bankAccount.isDefault = true;
        }

        switch (this.userType) {
            case DIRECT: {
                const response = await this._createBankAccountForSellerService.executeOnce(
                    new CreateBankAccountForSellerRequest({
                        bankAccount,
                        refSellerKey: options?.sellerKey
                    })
                );
                await this.listBankAccounts();

                return response.redactedBankAccount.bankAccountKey;
            }
            case PLATFORM: {
                const response = await this._createBankAccountForSellerService.executeOnce(
                    new CreateBankAccountForSellerRequest({
                        bankAccount,
                        refSellerKey: options?.sellerKey
                    })
                );

                if (isEnrolling) {
                    const relationship = (
                        await this._platformRelationshipService.relationshipDetails$
                            .pipe(first())
                            .toPromise()
                    ).relationship;

                    const request = new UpdateRelationshipBankAccountRequest({
                        relationshipKey: relationship.relationshipKey,
                        refBankAccountKey: response.redactedBankAccount.bankAccountKey
                    });
                    request.hasTermsOfService = true;
                    await this._updateRelationshipBankAccountService.executeOnce(request);
                }

                await this._platformRelationshipService.refreshRelationship();
                await this.listBankAccounts();

                return response.redactedBankAccount.bankAccountKey;
            }
        }
    }

    public async createIntlBankAccount(
        bankAccount: BankAccount,
        routingNumber: string,
        swiftCode: string,
        bankAddress: AccountRequestAddress,
        countryCode: string,
        useCompanyAddress: boolean,
        beneAddress: AccountRequestAddress,
        isEnrolling = false,
        options?: { sellerKey: string }
    ): Promise<Guid> {
        if (isEnrolling) {
            bankAccount.isDefault = true;
        }
        const response = await this._createInternationalBankAccountForSellerService.executeOnce(
            new CreateInternationalBankAccountForSellerRequest({
                bankAccount,
                routingNumber,
                swiftCode,
                bankAddress,
                countryCode,
                usePrimaryAddress: useCompanyAddress,
                beneficiaryAddress: !useCompanyAddress ? beneAddress : undefined,
                refSellerKey: options?.sellerKey
            })
        );
        await this.listBankAccounts();

        return response.redactedBankAccount.bankAccountKey;
    }

    public async submitBankAccountForVerification(
        bankAccountKey: Guid,
        sellerKey?: string
    ): Promise<void> {
        await this._submitBankAccountForVerificationService.executeOnce(
            new SubmitBankAccountForVerificationRequest({
                bankAccountKey,
                sellerKey
            })
        );
    }

    // call to payment domain
    public async verifyPaymentMethod(bankAccount: BankAccount): Promise<void> {
        bankAccount.refCompanyKey = this._userService.getCompanyKey();

        // posting new payment method so these are false
        bankAccount.active = false;
        bankAccount.isVerified = false;
        await this._postPaymentMethodService.execute(
            bankAccount.accountName,
            bankAccount.accountNumber,
            bankAccount.bankAccountType,
            bankAccount.bankName,
            bankAccount.refCompanyKey,
            bankAccount.active,
            bankAccount.isDefault,
            bankAccount.isVerified,
            bankAccount.bankAccountKey,
            bankAccount.routingNumber
        );
        return;
    }

    public async createBankAccountAndSubmitForVerification(
        bankAccount: BankAccount,
        isEnrolling = false,
        options?: { sellerKey: string }
    ): Promise<Guid> {
        const bankAccountKey = await this.createBankAccount(bankAccount, isEnrolling, {
            sellerKey: options?.sellerKey
        });
        await this.submitBankAccountForVerification(bankAccountKey, options?.sellerKey);
        return bankAccountKey;
    }

    public async createIntlBankAccountAndSubmitForVerification(
        bankAccount: BankAccount,
        swiftCode: string,
        routingNumber: string,
        bankAddress: AccountRequestAddress,
        countryCode: string,
        useCompanyAddress: boolean,
        beneAddress: AccountRequestAddress,
        isEnrolling = false,
        options?: { sellerKey: string }
    ): Promise<Guid> {
        const bankAccountKey = await this.createIntlBankAccount(
            bankAccount,
            routingNumber,
            swiftCode,
            bankAddress,
            countryCode,
            useCompanyAddress,
            beneAddress,
            isEnrolling,
            {
                sellerKey: options?.sellerKey
            }
        );
        await this.submitBankAccountForVerification(bankAccountKey, options?.sellerKey);
        return bankAccountKey;
    }

    public async updateSellerDefaultBankAccount(refBankAccountKey: Guid): Promise<void> {
        switch (this.userType) {
            case DIRECT: {
                await this._updateSellerDefaultBankAccountService.executeOnce(
                    new UpdateSellerDefaultBankAccountRequest({
                        refBankAccountKey
                    })
                );

                // update seller default status in payment domain
                if (this._featureFlagService.isOnSync(FeatureFlags.LegalEntityConnection)) {
                    // get all paymentMethods for company
                    let companyKey = this._userService.getCompanyKey();

                    this._getPaymentMethodByCompanyKeyService
                        .execute(companyKey)
                        .then(async (paymentMethods) => {
                            // if default is true, set to false
                            for (const paymentMethod of paymentMethods) {
                                if (paymentMethod.isDefault) {
                                    let isVerified = paymentMethod.isVerified;
                                    let isDefault = !paymentMethod.isDefault;
                                    await this._putPaymentMethodService.execute(
                                        paymentMethod.key,
                                        isVerified,
                                        isDefault
                                    );
                                }
                            }

                            // set updated payment method to default
                            let isVerified = false;
                            let isDefault = true;
                            await this._putPaymentMethodService.execute(
                                refBankAccountKey,
                                isVerified,
                                isDefault
                            );
                        });
                }

                await this.listBankAccounts();
                break;
            }
            case PLATFORM: {
                const { relationship } =
                    await this._platformRelationshipService.relationshipDetails$
                        .pipe(first())
                        .toPromise();

                await this._updateSellerDefaultBankAccountService.executeOnce(
                    new UpdateSellerDefaultBankAccountRequest({
                        refBankAccountKey
                    })
                );

                const request = new UpdateRelationshipBankAccountRequest({
                    relationshipKey: relationship.relationshipKey,
                    refBankAccountKey
                });

                try {
                    await this._updateRelationshipBankAccountService.executeOnce(request);
                    await this._platformRelationshipService.refreshRelationship();
                    await this.listBankAccounts();
                } catch {}
            }
        }
    }

    public async removeBankAccount(refBankAccountKey: Guid): Promise<void> {
        await this._deleteBankAccountForSellerService.executeOnce(
            new DeleteBankAccountForSellerRequest({
                refBankAccountKey
            })
        );

        // mark bank account as inactive in payment domain
        if (this._featureFlagService.isOnSync(FeatureFlags.LegalEntityConnection)) {
            await this._deletePaymentMethodService.execute(refBankAccountKey);
        }

        if (this.userType === PLATFORM) {
            await this._platformRelationshipService.refreshRelationship();
        }

        return this.listBankAccounts();
    }
}
